<script setup lang="ts">

import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import TagButton from '@/components/Elements/Buttons/TagButton.vue'
import { usePlanetStore } from '@/stores/planetStore.ts'
import { computed, defineProps } from 'vue'
import Backend from '@/models/backend.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { useAssetStore } from '@/stores/assetStore.ts'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Props
 */
const props = defineProps({
  planetId: {
    type: Number,
    required: true
  }
});

/**
 * Stores / models
 */
const planetStore = usePlanetStore();
const gameStore = useGameStore();
const assetStore = useAssetStore();

/**
 * Variables
 */
const list = [
  'all',
  'food',
  'production',
  'science',
  'credit',
  'energy',
  'energy_storage',
  'ore_storage',
  'scanning_range',
  'defence_attack_ships',
  'defence_shields',
  'ore_refinery',
  'ore'
];

/**
 * Functions
 */
const setSelectedType = (type: string) => {
  planetStore.planetProductionMenuType = type;
};

const toggleProductionMode = () => {
  if(planetStore.planetProductionDisplayMode !== 'production') {
    planetStore.planetProductionDisplayMode = 'production';
  } else {
    planetStore.planetProductionDisplayMode = '';
  }
};

const togglePQAMode = () => {
  if(planetStore.planetProductionDisplayMode !== 'pqa') {
    planetStore.planetProductionDisplayMode = 'pqa';
  } else {
    planetStore.planetProductionDisplayMode = '';
  }
};

const clearQueue = () => {
  gameStore.modal2Type = 'confirmationDelete';
  gameStore.modal2Data = {
    title: t('planets.production.clearConfirm.title'),
    message: t('planets.production.clearConfirm.message'),
    actionText: t('common.actions.yes'),
    cancelText: t('common.actions.no'),
    action: () => {
      const backend = new Backend();
      backend.clearProductionFromPlanet([planetData.value.id]);
      gameStore.setTinySuccessToastMessage(t('planets.production.queueCleared'));
    },
  };
};

/**
 * Computed
 */
const planetData = computed(() => planetStore.findPlanetById(props.planetId)!);

const filteredList = computed(() => {
  const productionObjectsMap = gameStore.playerProductionObjects;
  if (!productionObjectsMap || productionObjectsMap.size === 0) {
    return [];
  }

  // 1. Retrieve all production objects from the map
  let productionObjects = Array.from(productionObjectsMap.keys())
    .map((id) => {
      const productionObject = assetStore.findProductionObjectById(id);
      if (!productionObject) {
        throw new Error(`Production object with ID ${id} not found.`);
      }
      return productionObject;
    });

  // 2. Filter to include only buildings
  productionObjects = productionObjects.filter((obj) => obj.type === 'building');

  // 3. Filter out buildings that are already built, being built, or in the queue
  const planet = planetStore.findPlanetById(props.planetId);
  if (!planet) {
    throw new Error(`Planet with ID ${props.planetId} not found.`);
  }

  const { buildings = [], productionObjectId, productionQueue = [] } = planet;

  productionObjects = productionObjects.filter((obj) => {
    // Already constructed
    if (buildings.includes(obj.objectId)) return false;
    // Currently in production
    if (productionObjectId && obj.id === productionObjectId) return false;
    // In the production queue
    if (productionQueue.some((item) => item.productionObjectId === obj.id)) return false;
    return true;
  });

  // 4. Collect all effect types into a Set (avoids duplicates)
  const effectTypes = new Set<string>();

  // "all" is always included
  effectTypes.add('all');

  productionObjects.forEach((obj) => {
    const building = assetStore.findBuildingByCode(obj.objectId);
    if (!building) {
      throw new Error(`Building code ${obj.objectId} not found.`);
    }
    building.effects.forEach((effect: { type: string; bonus: number }) => {
      if (effect.bonus > 0) {
        effectTypes.add(effect.type);
      }
    });
  });

  // 5. Sort the found effect types according to the predefined 'list' order
  //    Only include items that exist in 'effectTypes'
  return list.filter((type) => effectTypes.has(type));
});


</script>

<template>
  <div class="w-12 pt-1">
    <!--- Action Buttons Container --->
    <div class="action-container rounded p-1 mb-2">
      <!--- Production Automation Cog --->
      <ActionButton
        @click="togglePQAMode"
        :forIcon="true"
        :active="planetStore.planetProductionDisplayMode === 'pqa'">
        <GenericIcon :tooltip="t('planets.production.menu.productionQueue')" type="cog"/>
      </ActionButton>
      <!--- Clear Production Queue --->
      <ActionButton
        @click="clearQueue"
        :forIcon="true">
        <GenericIcon :tooltip="t('planets.production.menu.clearQueue')" type="trash"/>
      </ActionButton>
      <!--- Production Mode Toggle --->
      <ActionButton
        @click="toggleProductionMode"
        :forIcon="true"
        :active="planetStore.planetProductionDisplayMode === 'production'">
        <GenericIcon :tooltip="t('planets.production.menu.selectProduction')" :type="'production'"/>
      </ActionButton>
    </div>
    <!--- Production Type Icons --->
    <template v-for="item in filteredList" :key="item">
      <TagButton
        v-if="planetStore.planetProductionDisplayMode === 'production'"
        @click="setSelectedType(item)"
        :selected="planetStore.planetProductionMenuType === item"
        class="mb-1 w-full justify-center">
        <GenericIcon v-if="item != 'all'" :type="item" :class="{'grayscale': planetStore.planetProductionMenuType !== item}"/>
        <span v-else class="text-sm font-medium">{{ t('planets.production.menu.all') }}</span>
      </TagButton>
    </template>
  </div>
</template>

<style scoped lang="scss">
.action-container {
  @apply bg-metal-900;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
</style>